import { createSelector, createSlice } from "@reduxjs/toolkit";

import { setDeep } from "Libs/objectAccess";
import logger from "Libs/logger";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";
import { getNewBillingSystemError } from "Hooks/billingSystem/getNewBillingSystemError";

import { getProfileEstimations } from "Api/billingSystem/costs/getProfileEstimations.request";

import type { ProfileEstimation } from "Api/billingSystem/types";
import type { RootState } from "Store/configureStore";

// Thunk
export const getProfileEstimationsThunk = createAppAsyncThunk(
  "billingSystem/profileEstimation/get",
  async (
    { billingProfileId }: { billingProfileId: string },
    { rejectWithValue }
  ) => {
    try {
      const estimations = await getProfileEstimations(billingProfileId);
      return estimations;
    } catch (err) {
      logger(err, {
        action: "get_profile_estimations",
        billingProfileId
      });
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

// State type
type ProfileEstimationState = {
  dataByProfileId: Record<
    string,
    {
      estimations?: ProfileEstimation;
      loading: boolean;
      error?: string;
    }
  >;
};

const initialState: ProfileEstimationState = {
  dataByProfileId: {}
};

// Slice
const profileEstimationSlice = createSlice({
  name: "profileEstimation",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getProfileEstimationsThunk.pending, (state, { meta }) => {
        const { billingProfileId } = meta.arg;
        setDeep(state, ["dataByProfileId", billingProfileId, "loading"], true);
        setDeep(
          state,
          ["dataByProfileId", billingProfileId, "error"],
          undefined
        );
      })
      .addCase(
        getProfileEstimationsThunk.fulfilled,
        (state, { meta, payload }) => {
          const { billingProfileId } = meta.arg;
          setDeep(
            state,
            ["dataByProfileId", billingProfileId, "loading"],
            false
          );
          setDeep(
            state,
            ["dataByProfileId", billingProfileId, "estimations"],
            payload
          );
        }
      )
      .addCase(
        getProfileEstimationsThunk.rejected,
        (state, { meta, payload }) => {
          const { billingProfileId } = meta.arg;
          setDeep(
            state,
            ["dataByProfileId", billingProfileId, "loading"],
            false
          );
          setDeep(
            state,
            ["dataByProfileId", billingProfileId, "error"],
            payload?.error
          );
        }
      );
  }
});

export default profileEstimationSlice.reducer;

// Selectors
const profileEstimationStateSelector = (state: RootState) =>
  state.profileEstimation;

export const selectProfileEstimation = createSelector(
  profileEstimationStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) =>
    state.dataByProfileId[billingProfileId]?.estimations
);

export const selectProfileEstimationLoading = createSelector(
  profileEstimationStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) =>
    state.dataByProfileId[billingProfileId]?.loading ?? false
);

export const selectProfileEstimationError = createSelector(
  profileEstimationStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) => state.dataByProfileId[billingProfileId]?.error
);
